import React, { useContext, HTMLAttributes } from 'react';
import classnames from 'classnames';
import { getForcedGlobalClassnamesForContext, getComponentName } from './helpers';
import { ThemeOptions, ThemeSkin, ThemeSurface } from './constants';
import { FlagContext } from './flags/FlagContext';

export interface WithThemeContextProps {
  theme?: ThemeOptions;
}

type ThemeVariant = ThemeOptions.DARK | ThemeOptions.LIGHT | 'green';

export const ThemeContext = React.createContext<ThemeOptions | undefined>(undefined);
ThemeContext.displayName = 'ThemeContext';

export const ThemeSkinContext = React.createContext<ThemeSkin | undefined>(undefined);
ThemeSkinContext.displayName = 'ThemeSkinContext';

export const ThemeSurfaceContext = React.createContext<ThemeSurface | undefined>(undefined);
ThemeSurfaceContext.displayName = 'ThemeSurfaceContext';

export function useSelectByTheme<TDark, TLight, TGreen = TLight>(config: {
  dark: TDark;
  light: TLight;
  green?: TGreen;
}) {
  const { dark, light, green = light } = config;
  const theme = useContext(ThemeContext);
  const themeSkin = useContext(ThemeSkinContext);

  if (theme === ThemeOptions.DARK) return dark;
  if (themeSkin === ThemeSkin.GREEN) return green ?? light;
  return light;
}

// HOC for convenience...
export function withThemeContext<TProps>(
  Component: React.ComponentType<TProps & WithThemeContextProps>
) {
  const DecoratedComponent: React.FC<TProps> = props => {
    const theme = useContext(ThemeContext);
    return <Component theme={theme} {...props} />;
  };
  DecoratedComponent.displayName = `withThemeContext(${getComponentName(Component)})`;
  return DecoratedComponent;
}

export interface ThemeOverrideProps extends HTMLAttributes<HTMLOrSVGElement> {
  theme?: ThemeOptions | 'match';
  themeSkin?: ThemeSkin;
  themeSurface?: ThemeSurface;
  className?: string;
  tag?: keyof React.JSX.IntrinsicElements | React.ComponentType;
  id?: string;
}

// and for overriding theme...
export const ThemeOverride = React.forwardRef<HTMLOrSVGElement, ThemeOverrideProps>(
  (
    {
      children,
      theme = 'match',
      themeSkin,
      themeSurface = ThemeSurface.PRIMARY,
      className,
      tag: Tag = 'span',
      ...rest
    },
    ref
  ) => {
    const flags = useContext(FlagContext);
    if (theme === 'match') {
      return (
        <Tag
          // Ignore here to suppress some editor specific complexity warnings
          // @ts-ignore
          ref={ref as any}
          className={classnames(className, 'surface', themeSurface)}
          {...rest}
        >
          {children}
        </Tag>
      );
    }

    return (
      <ThemeContext.Provider value={theme}>
        <Tag
          // We could remove these `as any` casts if we use a subset of the elements available
          // in `React.JSX.IntrinsicElements`'s type. That seems like more hassle than it's worth, though.
          ref={ref as any}
          className={classnames(
            className,
            getForcedGlobalClassnamesForContext({
              theme,
              themeSkin,
              themeSurface,
              flags,
            })
          )}
          {...rest}
        >
          {children}
        </Tag>
      </ThemeContext.Provider>
    );
  }
);
ThemeOverride.displayName = 'ThemeOverride';

export const useReverseTheme = () => {
  const theme = useContext(ThemeContext);
  return theme === ThemeOptions.LIGHT ? ThemeOptions.DARK : ThemeOptions.LIGHT;
};

export const ReverseTheme: React.FC<
  React.PropsWithChildren<{
    className?: string;
    tag?: keyof React.JSX.IntrinsicElements;
  }>
> = ({ children, className, tag }) => {
  const flippedTheme = useReverseTheme();
  return (
    <ThemeOverride theme={flippedTheme} className={className} tag={tag}>
      {children}
    </ThemeOverride>
  );
};
ReverseTheme.displayName = 'ReverseTheme';

// re-export to avoid changing a billion imports again...
export { ThemeOptions, ThemeSkin, ThemeSurface, type ThemeVariant };
